import React, { useState } from "react";
import valid from "card-validator";
import ErrorDialog from "~/components/common/dialog";
import Utils from "~/helpers/Utils";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import _ from "lodash";
import useStyles from "./style";

const PLACEHOLDER_COLOR = "#BBBBB0";

// autoComplete/name com os tokens cc-* fazem o iOS e o Android oferecerem o cartão salvo
const CardInput = ({ label, placeholder, value, onChange, maxLength, inputMode, autoComplete, name, className, style }) => {
	const classes = useStyles();
	return (
		<div className={className || classes.cardFieldWrapper}>
			<label className={classes.cardFieldLabel}>{label}</label>
			<input
				className={classes.cardFieldInput}
				placeholder={placeholder}
				value={value}
				onChange={onChange}
				maxLength={maxLength}
				inputMode={inputMode}
				autoComplete={autoComplete}
				name={name}
				style={{ ...style }}
			/>
		</div>
	);
};

const DialogAddCard = ({ onClose, openNext, setCardData }) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const [number, setNumber] = useState("");
	const [expiry, setExpiry] = useState("");
	const [cvc, setCVC] = useState("");
	const [name, setName] = useState("");
	const [cpf, setCPF] = useState("");
	const [email, setEmail] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [openError, setOpenError] = useState(false);
	const [cpfError, setCpfError] = useState("");
	const { user } = useSelector((store) => store.auth);
	const { business } = useSelector((store) => store.business);
	const meStore = useSelector((store) => store.me);

	const currentUser = meStore?.me || user || {};
	const isOwnerUser = !_.isEmpty(currentUser?.id) && !_.isEmpty(business?.ownerId) && currentUser.id === business.ownerId;

	const cardIsValid = () => {
		const rawNumber = number.replace(/\s/g, "");
		if (rawNumber !== "" && expiry !== "" && cvc !== "" && name !== "" && cpf !== "" && email !== "") {
			let validationNumber = valid.number(rawNumber).isValid;
			let validationCVC = valid.cvv(cvc).isValid;
			let validationDate = valid.expirationDate(expiry).isValid;
			return validationNumber && validationCVC && validationDate;
		}
		return false;
	};

	const fieldsAreFilled = () =>
		number.replace(/\s/g, "").length === 16 && expiry.length === 5 && cvc.length === 3 && name.trim() !== "" && cpf.length === 14 && email.trim() !== "";

	const handleScheduleClick = () => {
		let emptyFields = [];

		if (!Utils.validateCPF(cpf.replace(/[^0-9]/g, ""))) {
			setCpfError(t("Document on card"));
			return;
		}

		if (number === "") emptyFields.push(t("Card number"));
		if (expiry === "") emptyFields.push(t("Validity"));
		if (cvc === "") emptyFields.push(t("CVC"));
		if (name === "") emptyFields.push(t("Name on card"));
		if (cpf === "") emptyFields.push(t("Document on card"));
		if (email === "") emptyFields.push(t("Email used to pay"));

		if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email.trim())) {
			emptyFields.push(t("Email used to pay"));
		}

		if (emptyFields.length > 0) {
			setOpenError(true);
			setErrorMessage(t("List of empty fields", { empty_fields_list: emptyFields.join(", ") }));
			return;
		}

		if (cardIsValid()) {
			if (isOwnerUser) {
				mixpanelTrackEvent("Adicionar_Meio_De_Pagamento", { Meio_De_Pagamento: "Crédito" }, currentUser, business);
			}
			if (onClose) onClose();
			if (setCardData) {
				setCardData({
					number: number.replace(/\s/g, ""),
					name,
					expiry: expiry.replace(/[^0-9]/g, ""),
					cvc,
					cpf: cpf.replace(/[^0-9]/g, ""),
					email,
				});
			}
			if (openNext) openNext();
		} else {
			setOpenError(true);
			setErrorMessage(t("Error Card Not Valid"));
		}
	};

	const cpfMask = (value) =>
		value
			.replace(/\D/g, "")
			.replace(/(\d{3})(\d)/, "$1.$2")
			.replace(/(\d{3})(\d)/, "$1.$2")
			.replace(/(\d{3})(\d{1,2})/, "$1-$2")
			.replace(/(-\d{2})\d+?$/, "$1");

	// o preenchimento automático pode trazer MM/AAAA: mantém só os dois dígitos finais do ano
	const dateMask = (value) => {
		const digits = value.replace(/\D/g, "");
		const mmyy = digits.length === 6 ? digits.slice(0, 2) + digits.slice(4) : digits.slice(0, 4);
		return mmyy.replace(/(\d{2})(\d)/, "$1/$2");
	};

	const cardNumberMask = (value) =>
		value.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");

	const inputStyle = { "::placeholder": { color: PLACEHOLDER_COLOR } };

	return (
		<>
			<ErrorDialog
				onOpen={openError}
				message={errorMessage}
				onOk={() => setOpenError(false)}
				title={t("Attention")}
			/>

			<div className={classes.pixConfirmContainer}>
				{/* Header */}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
					<span style={{ fontSize: 16, fontWeight: 600, color: "#222" }}>Adicionar cartão</span>
					<button
						style={{
							background: "none",
							border: "none",
							fontSize: 18,
							color: "#999",
							cursor: "pointer",
							padding: 4,
							lineHeight: 1,
						}}
						onClick={onClose}
					>
						&#x2715;
					</button>
				</div>

				{/* Número do cartão */}
				<CardInput
					label="Número do cartão"
					placeholder="0000 0000 0000 0000"
					value={number}
					onChange={(e) => setNumber(cardNumberMask(e.target.value))}
					maxLength={19}
					inputMode="numeric"
					autoComplete="cc-number"
					name="cardnumber"
				/>

				{/* Validade + CVC */}
				<div className={classes.cardRowFields}>
					<div>
						<label className={classes.cardFieldLabel}>Validade</label>
						<input
							className={classes.cardFieldInput}
							placeholder="MM/AA"
							value={expiry}
							onChange={(e) => setExpiry(dateMask(e.target.value))}
							maxLength={5}
							inputMode="numeric"
							autoComplete="cc-exp"
							name="cc-exp"
						/>
					</div>
					<div style={{ marginLeft: 10 }}>
						<label className={classes.cardFieldLabel}>CVC</label>
						<input
							className={classes.cardFieldInput}
							placeholder="000"
							onChange={(e) => setCVC(e.target.value)}
							maxLength={3}
							inputMode="numeric"
							autoComplete="cc-csc"
							name="cvc"
						/>
					</div>
				</div>

				{/* Nome */}
				<CardInput
					label="Nome"
					placeholder="Como está no cartão"
					value={name}
					onChange={(e) => setName(e.target.value)}
					autoComplete="cc-name"
					name="ccname"
				/>

				{/* CPF */}
				<div className={classes.cardFieldWrapper}>
					<label className={classes.cardFieldLabel}>CPF</label>
					<input
						className={classes.cardFieldInput}
						placeholder="000.000.000-00"
						value={cpf}
						onChange={(e) => setCPF(cpfMask(e.target.value))}
						maxLength={14}
						inputMode="numeric"
					/>
					{cpfError && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>{cpfError}</span>}
				</div>

				{/* E-mail */}
				<CardInput
					label="E-mail"
					placeholder="voce@email.com"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					inputMode="email"
					autoComplete="email"
					name="email"
				/>

				{/* Botão confirmar */}
				<button
					className={classes.cardConfirmButton}
					onClick={handleScheduleClick}
					onKeyPress={(e) => e.key === "Enter" && handleScheduleClick()}
					style={{ backgroundColor: fieldsAreFilled() ? "#672E37" : "#C4909A" }}
				>
					Confirmar pagamento
				</button>
			</div>
		</>
	);
};

export default DialogAddCard;
